import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { ActionCreator, AnyAction } from 'redux';
import type { SetRequired } from 'type-fest';

import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { FormLabel } from '../../generic';
import FilterCard from '../../generic/Filter/FilterCard';
import useFetchPackageVersionProjects from '../hooks/useFetchPackageVersionProjects';
import type { PackageInventoryParams } from '../PackageInventoryContainer';

import PackageLabelsFilter from './PackageLabelsFilter';

type PackageFiltersForVersionProps = {
  arePackageLabelsLoading: boolean;
  packageLabelOptions: string[];
  selectedPackageLabels: string[];
  setFilters: (...args: unknown[]) => void;
};

/**
 * PackageFiltersForVersion has contains all filters related to filtering projects for a given package version.
 * It currently includes package labels only.
 */
const PackageFiltersForVersion: VoidFunctionComponent<PackageFiltersForVersionProps> = ({
  arePackageLabelsLoading,
  packageLabelOptions,
  selectedPackageLabels,
  setFilters,
}) => (
  <FilterCard id="package-inventory-package-filters" title="Package">
    <FormLabel label="Labels">
      <PackageLabelsFilter
        isLoading={arePackageLabelsLoading}
        packageLabelOptions={packageLabelOptions}
        selectedPackageLabels={selectedPackageLabels}
        setFilters={setFilters}
      />
    </FormLabel>
  </FilterCard>
);

type PackageVersionFiltersPackagesContainerProps = {
  /**
   * Package locator and version are required to filter projects for a given package version.
   */
    params: SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>;
  };

/**
 * Stateful container for the package filters for a given package version.
 */
export const PackageVersionFiltersPackagesContainer: VoidFunctionComponent<
  PackageVersionFiltersPackagesContainerProps
> = ({ params }) => {
  // Load org package labels with the useOrganizationPackageLabels hook.
  const { data: packageLabelsResponse, isLoading: arePackageLabelsLoading } = useOrganizationPackageLabels();
  const packageLabels = packageLabelsResponse?.packageLabels ?? [];
  const packageLabelOptions = packageLabels.map((packageLabel) => packageLabel.name);

  const selectedPackageLabels = useSelector(PackageInventoryStore.selectors.getFiltersPackageLabels);

  const setFilters = useFetchPackageVersionProjects(
    PackageInventoryStore.actions.setFilters as unknown as ActionCreator<AnyAction>,
    params.packageLocator,
    params.version,
  );

  return (
    <PackageFiltersForVersion
      arePackageLabelsLoading={arePackageLabelsLoading}
      packageLabelOptions={packageLabelOptions}
      selectedPackageLabels={selectedPackageLabels}
      setFilters={setFilters}
    />
  );
};
